import React, { useState, useEffect } from "react";
import api from "../lib/api";
import { useNavigate } from "react-router-dom";
import { Edit2, Trash2, X, Save, MessageSquare, ChevronDown, ChevronRight, ExternalLink, Send } from "lucide-react";
import { useConfirm } from "../contexts/ConfirmContext";

const STATUS_OPTIONS = [
  { value: "open", label: "Offen", cls: "status-open" },
  { value: "in_progress", label: "In Bearbeitung", cls: "status-in-progress" },
  { value: "done", label: "Erledigt", cls: "status-done" },
];

const PRIORITY_OPTIONS = [
  { value: "low", label: "Niedrig" },
  { value: "medium", label: "Mittel" },
  { value: "high", label: "Hoch" },
];

// ── Draggable Card ─────────────────────────────────────────────────────
function GlobalTicketCard({ ticket, users, onDelete, onUpdate, navigate }) {
  const [dragging, setDragging] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ title: ticket.title, description: ticket.description || "", priority: ticket.priority, assigned_to: ticket.assigned_to || "" });
  const [commentText, setCommentText] = useState("");

  const save = async (e) => {
    e.preventDefault();
    const r = await api.put(`/customers/${ticket.customer_id}/tickets/${ticket._id}`, form);
    onUpdate(r.data);
    setEditing(false);
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    await api.post(`/customers/${ticket.customer_id}/tickets/${ticket._id}/comments`, { text: commentText });
    const r2 = await api.get("/tickets").catch(() => null);
    if (r2) {
      const updated = r2.data.find(t => t._id === ticket._id);
      if (updated) onUpdate(updated);
    }
    setCommentText("");
  };

  return (
    <div
      data-testid={`global-ticket-${ticket._id}`}
      draggable
      onDragStart={e => {
        e.dataTransfer.setData("ticketId", ticket._id);
        e.dataTransfer.setData("customerId", ticket.customer_id);
        e.dataTransfer.effectAllowed = "move";
        setDragging(true);
      }}
      onDragEnd={() => setDragging(false)}
      className={`kanban-card bg-card border border-border mb-2 cursor-grab active:cursor-grabbing transition-opacity ${dragging ? "opacity-40" : ""}`}
    >
      <div className="p-3">
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm leading-snug">{ticket.title}</p>
            {ticket.customer_name && (
              <button
                onClick={() => navigate(`/customers/${ticket.customer_id}`)}
                className="text-xs text-primary flex items-center gap-0.5 hover:underline mt-0.5"
              >
                {ticket.customer_name} <ExternalLink size={9} />
              </button>
            )}
            {ticket.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{ticket.description}</p>}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button onClick={() => { setEditing(!editing); setExpanded(true); }} className="text-muted-foreground hover:text-primary transition-colors p-0.5">
              <Edit2 size={12} />
            </button>
            <button onClick={() => onDelete(ticket._id, ticket.customer_id)} className="text-muted-foreground hover:text-destructive transition-colors p-0.5">
              <Trash2 size={12} />
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-2">
            <span className={`text-xs font-semibold px-1.5 py-0.5 priority-${ticket.priority}`}>
              {PRIORITY_OPTIONS.find(p => p.value === ticket.priority)?.label}
            </span>
            {ticket.assigned_initials && (
              <span className="initials-avatar w-5 h-5 text-xs" title={ticket.assigned_name}>{ticket.assigned_initials}</span>
            )}
          </div>
          <button
            data-testid={`toggle-comments-${ticket._id}`}
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            title="Kommentare"
          >
            <MessageSquare size={11} />{ticket.comments?.length || 0}
            {expanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
          </button>
        </div>
      </div>

      {editing && (
        <div className="border-t border-border p-3 bg-muted/20">
          <form onSubmit={save} className="space-y-2">
            <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required
              className="w-full px-2 py-1.5 text-sm bg-background border border-input focus:border-primary focus:outline-none" />
            <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2}
              className="w-full px-2 py-1.5 text-sm bg-background border border-input focus:border-primary focus:outline-none resize-none" />
            <div className="flex gap-2">
              <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}
                className="flex-1 px-2 py-1.5 text-xs bg-background border border-input focus:border-primary focus:outline-none">
                {PRIORITY_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
              <select value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })}
                className="flex-1 px-2 py-1.5 text-xs bg-background border border-input focus:border-primary focus:outline-none">
                <option value="">Nicht zugewiesen</option>
                {users.map(u => <option key={u._id} value={u._id}>{u.name} ({u.initials})</option>)}
              </select>
            </div>
            <div className="flex gap-1.5">
              <button type="submit" className="px-2 py-1 bg-primary text-primary-foreground text-xs font-semibold flex items-center gap-1"><Save size={11} /> Speichern</button>
              <button type="button" onClick={() => setEditing(false)} className="px-2 py-1 bg-secondary text-xs font-semibold flex items-center gap-1"><X size={11} /> Abbrechen</button>
            </div>
          </form>
        </div>
      )}

      {expanded && (
        <div className="border-t border-border p-3 bg-muted/20 space-y-2">
          {ticket.comments?.length > 0 ? (
            ticket.comments.map((c, i) => (
              <div key={i} className="flex gap-2 text-xs">
                <span className="initials-avatar w-5 h-5 text-xs flex-shrink-0">{c.user_initials}</span>
                <div>
                  <span className="font-semibold">{c.user_name}</span>
                  <span className="text-muted-foreground ml-2">{new Date(c.created_at).toLocaleString("de-CH")}</span>
                  <p className="mt-0.5">{c.text}</p>
                </div>
              </div>
            ))
          ) : (
            <p className="text-xs text-muted-foreground italic">Noch keine Kommentare</p>
          )}
          <form onSubmit={addComment} className="flex gap-2 mt-2">
            <input
              data-testid={`comment-input-${ticket._id}`}
              value={commentText}
              onChange={e => setCommentText(e.target.value)}
              placeholder="Kommentar hinzufügen..."
              className="flex-1 px-2 py-1 text-xs bg-background border border-input focus:border-primary focus:outline-none"
            />
            <button type="submit" className="px-2 py-1 bg-primary text-primary-foreground text-xs font-semibold flex items-center gap-1">
              <Send size={11} /> Senden
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

// ── Drop Column ────────────────────────────────────────────────────────
function GlobalKanbanColumn({ status, label, cls, tickets, users, onDrop, onDelete, onUpdate, navigate }) {
  const [dragOver, setDragOver] = useState(false);

  return (
    <div
      className={`kanban-col p-3 transition-all duration-150 ${dragOver ? "ring-2 ring-primary/40 bg-primary/5" : ""}`}
      onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOver(true); }}
      onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); }}
      onDrop={e => {
        e.preventDefault();
        setDragOver(false);
        const ticketId = e.dataTransfer.getData("ticketId");
        const customerId = e.dataTransfer.getData("customerId");
        if (ticketId && customerId) onDrop(ticketId, customerId, status);
      }}
      data-testid={`global-kanban-col-${status}`}
    >
      <div className="flex items-center justify-between mb-3">
        <span className={`text-xs font-semibold px-2 py-0.5 ${cls}`}>{label}</span>
        <span className="text-xs font-mono text-muted-foreground">{tickets.length}</span>
      </div>
      {tickets.map(t => (
        <GlobalTicketCard
          key={t._id} ticket={t} users={users}
          onDelete={onDelete} onUpdate={onUpdate} navigate={navigate}
        />
      ))}
      {tickets.length === 0 && (
        <div className={`border-2 border-dashed border-border rounded-sm p-4 text-center text-xs text-muted-foreground ${dragOver ? "border-primary/50" : ""}`}>
          Hierher ziehen
        </div>
      )}
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────
export default function GlobalTicketsPage() {
  const confirm = useConfirm();
  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterUser, setFilterUser] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      const [t, u] = await Promise.all([
        api.get("/tickets", { params }),
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      setTickets(t.data);
      setUsers(u.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filterStatus]);

  const remove = async (id, customerId) => {
    if (!(await confirm({ title: "Ticket löschen", message: "Ticket inkl. Kommentare wird gelöscht.", confirmLabel: "Löschen" }))) return;
    await api.delete(`/customers/${customerId}/tickets/${id}`);
    setTickets(tickets.filter(t => t._id !== id));
  };

  const update = (updated) => setTickets(prev => prev.map(t => t._id === updated._id ? updated : t));

  const handleDrop = async (ticketId, customerId, newStatus) => {
    const ticket = tickets.find(t => t._id === ticketId);
    if (!ticket || ticket.status === newStatus) return;
    // Optimistic update
    setTickets(prev => prev.map(t => t._id === ticketId ? { ...t, status: newStatus } : t));
    try {
      const r = await api.put(`/customers/${customerId}/tickets/${ticketId}`, { status: newStatus });
      setTickets(prev => prev.map(t => t._id === ticketId ? { ...r.data, customer_name: ticket.customer_name } : t));
    } catch {
      setTickets(prev => prev.map(t => t._id === ticketId ? ticket : t)); // rollback
    }
  };

  const filtered = filterUser ? tickets.filter(t => t.assigned_to === filterUser) : tickets;
  const byStatus = (status) => filtered.filter(t => t.status === status);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Alle Tickets</h1>
          <p className="text-sm text-muted-foreground mt-1">{filtered.length} Tickets über alle Kunden — per Drag &amp; Drop verschieben</p>
        </div>
        <div className="flex gap-2">
          <select
            data-testid="global-tickets-status-filter"
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none"
          >
            <option value="">Alle Status</option>
            {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select
            data-testid="global-tickets-user-filter"
            value={filterUser}
            onChange={e => setFilterUser(e.target.value)}
            className="px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none"
          >
            <option value="">Alle Mitarbeiter</option>
            {users.map(u => <option key={u._id} value={u._id}>{u.name} ({u.initials})</option>)}
          </select>
          <button onClick={load} className="px-3 py-2 bg-secondary text-secondary-foreground text-sm font-semibold hover:bg-secondary/80">
            Aktualisieren
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground text-center py-8">Laden...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="global-kanban-board">
          {STATUS_OPTIONS.map(s => (
            <GlobalKanbanColumn
              key={s.value} status={s.value} label={s.label} cls={s.cls}
              tickets={byStatus(s.value)} users={users}
              onDrop={handleDrop} onDelete={remove} onUpdate={update} navigate={navigate}
            />
          ))}
        </div>
      )}
    </div>
  );
}
